import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { toLocalInput } from "@/lib/utils-app";

interface Tournament { id: string; name: string; year: number }
interface Match { id: string; tournament_id: string; home_team: string; away_team: string }

interface Bet {
  id?: string;
  tournament_id: string;
  title_cs: string;
  title_en: string;
  description_cs: string | null;
  description_en: string | null;
  answer_type: "team" | "number";
  category: string;
  default_points: number;
  lock_on_first_match: boolean;
  lock_at: string | null;
  sort_order: number;
  correct_answer_text: string | null;
  correct_answer_number: number | null;
  is_published: boolean;
}

const emptyBet = (tid: string, order: number): Bet => ({
  tournament_id: tid,
  title_cs: "",
  title_en: "",
  description_cs: "",
  description_en: "",
  answer_type: "team",
  category: "special",
  default_points: 2,
  lock_on_first_match: true,
  lock_at: null,
  sort_order: order,
  correct_answer_text: null,
  correct_answer_number: null,
  is_published: true,
});

export const SpecialBetsAdmin = ({ tournaments, matches, selected, setSelected }: {
  tournaments: Tournament[]; matches: Match[]; selected: string; setSelected: (v: string) => void;
}) => {
  const [bets, setBets] = useState<Bet[]>([]);
  const [draft, setDraft] = useState<Bet | null>(null);
  const [loading, setLoading] = useState(false);

  const teams = useMemo(() => {
    const s = new Set<string>();
    matches.filter((m) => m.tournament_id === selected).forEach((m) => { s.add(m.home_team); s.add(m.away_team); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [matches, selected]);

  const load = async () => {
    if (!selected) return;
    setLoading(true);
    const { data } = await supabase.from("special_bets").select("*").eq("tournament_id", selected).order("sort_order");
    setBets((data as Bet[]) ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected]);

  const persist = async (bet: Bet) => {
    if (!bet.title_cs.trim() || !bet.title_en.trim()) { toast.error("Vyplň název česky i anglicky"); return; }
    const payload = {
      tournament_id: bet.tournament_id,
      title_cs: bet.title_cs.trim(),
      title_en: bet.title_en.trim(),
      description_cs: bet.description_cs?.trim() || null,
      description_en: bet.description_en?.trim() || null,
      answer_type: bet.answer_type,
      category: bet.category,
      default_points: Math.max(0, bet.default_points),
      lock_on_first_match: bet.lock_on_first_match,
      lock_at: bet.lock_at || null,
      sort_order: bet.sort_order,
      correct_answer_text: bet.answer_type === "team" ? (bet.correct_answer_text || null) : null,
      correct_answer_number: bet.answer_type === "number" ? bet.correct_answer_number : null,
      is_published: bet.is_published,
    };
    const { error } = bet.id
      ? await supabase.from("special_bets").update(payload).eq("id", bet.id)
      : await supabase.from("special_bets").insert(payload);
    if (error) { toast.error(error.message); return; }
    toast.success("Uloženo");
    setDraft(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Opravdu smazat tento speciální tip včetně odpovědí hráčů?")) return;
    const { error } = await supabase.from("special_bets").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Smazáno"); load(); }
  };

  const Form = ({ bet, onCancel }: { bet: Bet; onCancel: () => void }) => {
    const [b, setB] = useState<Bet>(bet);
    const set = (patch: Partial<Bet>) => setB((x) => ({ ...x, ...patch }));
    return (
      <Card className="border-primary/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{b.id ? "Upravit speciální tip" : "Nový speciální tip"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label className="text-xs">Název (CS)</Label>
              <Input value={b.title_cs} onChange={(e) => set({ title_cs: e.target.value })} className="h-10" /></div>
            <div className="space-y-1"><Label className="text-xs">Název (EN)</Label>
              <Input value={b.title_en} onChange={(e) => set({ title_en: e.target.value })} className="h-10" /></div>
            <div className="space-y-1"><Label className="text-xs">Popis (CS)</Label>
              <Input value={b.description_cs ?? ""} onChange={(e) => set({ description_cs: e.target.value })} className="h-10" /></div>
            <div className="space-y-1"><Label className="text-xs">Popis (EN)</Label>
              <Input value={b.description_en ?? ""} onChange={(e) => set({ description_en: e.target.value })} className="h-10" /></div>
            <div className="space-y-1"><Label className="text-xs">Typ odpovědi</Label>
              <Select value={b.answer_type} onValueChange={(v) => set({ answer_type: v as Bet["answer_type"] })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="team">Tým</SelectItem>
                  <SelectItem value="number">Číslo</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Kategorie</Label>
              <Select value={b.category} onValueChange={(v) => set({ category: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="teams">Týmy</SelectItem>
                  <SelectItem value="special">Speciální</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">Doporučené body</Label>
              <Input type="number" min={0} value={b.default_points}
                onChange={(e) => set({ default_points: Math.max(0, parseInt(e.target.value) || 0) })} className="h-10" /></div>
            <div className="space-y-1"><Label className="text-xs">Pořadí</Label>
              <Input type="number" value={b.sort_order}
                onChange={(e) => set({ sort_order: parseInt(e.target.value) || 0 })} className="h-10" /></div>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
            <Label className="cursor-pointer text-sm">Uzamknout startem prvního zápasu turnaje</Label>
            <Switch checked={b.lock_on_first_match} onCheckedChange={(v) => set({ lock_on_first_match: v })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Vlastní uzávěrka (volitelné)</Label>
            <Input type="datetime-local" value={b.lock_at ? toLocalInput(b.lock_at) : ""}
              onChange={(e) => set({ lock_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="h-10" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Správná odpověď (po vyhodnocení)</Label>
            {b.answer_type === "team" ? (
              <Select value={b.correct_answer_text ?? ""} onValueChange={(v) => set({ correct_answer_text: v })}>
                <SelectTrigger className="h-10"><SelectValue placeholder="Vyber tým" /></SelectTrigger>
                <SelectContent>{teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input type="number" value={b.correct_answer_number ?? ""}
                onChange={(e) => set({ correct_answer_number: e.target.value === "" ? null : parseInt(e.target.value) })}
                className="h-10" />
            )}
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
            <Label className="cursor-pointer text-sm">Zveřejněno hráčům</Label>
            <Switch checked={b.is_published} onCheckedChange={(v) => set({ is_published: v })} />
          </div>

          <div className="flex gap-2">
            <Button onClick={() => persist(b)} className="rounded-full">Uložit</Button>
            <Button variant="ghost" onClick={onCancel} className="rounded-full">Zrušit</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Speciální tipy</CardTitle>
        <CardDescription>Otázky mimo tipování zápasů (nejlepší tým, nejvíce gólů…). Po zadání správné odpovědi se body přepočítají ve všech ligách.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Turnaj</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-10 max-w-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{tournaments.map((tr) => <SelectItem key={tr.id} value={tr.id}>{tr.name} · {tr.year}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {draft ? (
          <Form bet={draft} onCancel={() => setDraft(null)} />
        ) : (
          <Button onClick={() => setDraft(emptyBet(selected, bets.length))} disabled={!selected} className="rounded-full">
            <Plus className="h-4 w-4 mr-2" />Přidat speciální tip
          </Button>
        )}

        {loading ? <p className="text-sm text-muted-foreground">Načítám…</p> : bets.length === 0 ? (
          <p className="text-sm text-muted-foreground">Pro tento turnaj zatím nejsou žádné speciální tipy.</p>
        ) : (
          <div className="space-y-2">
            {bets.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{b.title_cs}</p>
                  <p className="text-xs text-muted-foreground">
                    {b.category === "teams" ? "Týmy" : "Speciální"} · {b.answer_type === "team" ? "tým" : "číslo"} · {b.default_points} b.
                    {b.correct_answer_text || b.correct_answer_number != null
                      ? ` · výsledek: ${b.correct_answer_text ?? b.correct_answer_number}`
                      : " · bez výsledku"}
                    {b.is_published ? "" : " · skryto"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDraft(b)}>Upravit</Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(b.id!)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
